/*
 * Experiment 042 - Product - Styled on Real Walls (PostHog flag: 042-product-real-walls-row)
 *
 * control: the testimonial slideshow is untouched.
 * test:    the slideshow's slot holds "Styled on Real Walls", a row of real photos of our art on customers' and
 *          creators' walls. Desktop shows whole cards with arrows; phones swipe, with the next card peeking. A card
 *          opens a view of its photo with the words and the piece in it, which links on to the product page; the
 *          view steps through every card. The card's own link remains for new-tab clicks and as the fallback.
 *
 * The row is server-rendered by snippets/w99-042-real-walls (an inert <template>, so control loads none of it) and
 * placed here for the test only.
 * Eligibility: the slideshow section and at least four cards on the page. The flag is read as the page loads, so every
 * product-page visitor is exposed. Both arms then send real_walls_slot_reached once per page view when the visitor
 * scrolls to within SLOT_MARGIN px of the slot. PostHog's activation event narrows the analysis to those visitors,
 * so one experiment can report either population.
 * In the view, the credit links to the original in a new tab: a creator's Instagram post, or a buyer's review opened in
 * Loox's own quick view (?qv=<review ID> on the product page).
 * Events (not sent for forced previews): real_walls_slot_reached {$feature/042-product-real-walls-row, layout}, both
 * arms, only for a variant PostHog assigned; real_walls_row_impression {cards, layout} once per page view when half the row
 * is in view; real_walls_view_opened {product, position, credit, via, layout} each time the view shows a photo (via:
 * card, arrow, key, swipe); real_walls_product_click {product, position, credit, layout} from the view's piece card;
 * real_walls_source_click {product, position, credit, source, layout} from the view's credit link.
 * Preview: ?042-product-real-walls-row=test or ?042-product-real-walls-row=control
 * Diagnostics: one "[042-product-real-walls-row]" console line per exposure; add ?w99-debug=1 for the experiment
 * library's step-by-step log.
 */
(function () {
  'use strict';

  var FLAG = '042-product-real-walls-row';
  var VARIANTS = ['control', 'test'];
  var SECTION_SELECTOR = '[id^="shopify-section-"][id$="__163239441531ffb0ca"]';
  var TEMPLATE_ID = 'w99-042-real-walls';
  var TEST_CLASS = 'w99-042-test';
  var DESKTOP_QUERY = '(min-width: 769px)';
  var SLOT_MARGIN = 400;
  var MIN_CARDS = 4;
  var OPEN_CLASS = 'w99-042-view-open';
  var SWIPE_MIN = 40;
  var CHEVRON_PREV = '<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
  var CHEVRON_NEXT = '<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
  var EXTERNAL = '<svg class="w99-042-view__external" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false"><path d="M3 1h6v6M9 1L1.5 8.5" fill="none" stroke="currentColor" stroke-width="1.3"/></svg>';
  var CLOSE = '<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
  var FORCED = new URLSearchParams(window.location.search).get(FLAG);
  var IS_FORCED = VARIANTS.indexOf(FORCED) > -1;
  var DEBUG = /[?&]w99-debug(=|&|$)/.test(window.location.search);
  var REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function getSection() {
    var matches = document.querySelectorAll(SECTION_SELECTOR);
    return matches.length === 1 ? matches[0] : null;
  }

  function getTemplate() {
    var template = document.getElementById(TEMPLATE_ID);
    return template && template.content ? template : null;
  }

  function layout() {
    return window.matchMedia && window.matchMedia(DESKTOP_QUERY).matches ? 'desktop' : 'mobile';
  }

  function capture(event, properties) {
    if (IS_FORCED) return;
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') window.posthog.capture(event, properties);
    } catch (error) {}
  }

  function onFrame(fn) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () { queued = false; fn(); });
    };
  }

  function isEligible() {
    var section = getSection();
    var template = getTemplate();
    if (!section || !template) return false;
    return template.content.querySelectorAll('.w99-042-rw__card').length >= MIN_CARDS;
  }

  // Resolves once the slot's top is within SLOT_MARGIN px below the viewport, or already above it.
  function whenNear(element) {
    return new Promise(function (resolve) {
      if (!('IntersectionObserver' in window)) { resolve(true); return; }
      var observer = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        if (entry.isIntersecting || entry.boundingClientRect.top < 0) {
          observer.disconnect();
          resolve(true);
        }
      }, { rootMargin: '0px 0px ' + SLOT_MARGIN + 'px 0px' });
      observer.observe(element);
    });
  }

  // The arrows move the track by one screenful of whole cards; each disables at its end of the row.
  function wireArrows(row) {
    var track = row.querySelector('.w99-042-rw__track');
    var prev = row.querySelector('[data-w99-042-step="-1"]');
    var next = row.querySelector('[data-w99-042-step="1"]');
    if (!track || !prev || !next) return;
    var update = function () {
      var max = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max - 2;
      row.classList.toggle('w99-042-rw--static', max <= 2);
    };
    var page = function (direction) {
      var first = track.querySelector('.w99-042-rw__item');
      if (!first) return;
      var gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      var step = first.getBoundingClientRect().width + gap;
      var perView = Math.max(1, Math.floor((track.clientWidth + gap + 1) / step));
      track.scrollBy({ left: direction * perView * step, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
    };
    prev.addEventListener('click', function () { page(-1); });
    next.addEventListener('click', function () { page(1); });
    track.addEventListener('scroll', onFrame(update), { passive: true });
    window.addEventListener('resize', onFrame(update));
    update();
  }

  function create(tag, className) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    return element;
  }

  function text(element) {
    return element ? element.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  // One entry per card, read from the server-rendered markup.
  function readCards(row) {
    return Array.prototype.map.call(row.querySelectorAll('.w99-042-rw__card'), function (card) {
      var image = card.querySelector('img');
      return {
        card: card,
        product: card.getAttribute('data-w99-042-product'),
        credit: card.getAttribute('data-w99-042-credit') || '',
        creditHtml: (card.querySelector('.w99-042-rw__credit') || {}).innerHTML || '',
        source: card.getAttribute('data-w99-042-source') || '',
        sourceUrl: card.getAttribute('data-w99-042-source-url') || '',
        quote: text(card.querySelector('.w99-042-rw__quote')),
        zoom: card.getAttribute('data-w99-042-zoom'),
        zoomSet: card.getAttribute('data-w99-042-zoom-set'),
        alt: image ? image.getAttribute('alt') || '' : '',
        piece: card.parentNode.querySelector('template[data-w99-042-piece]')
      };
    });
  }

  function wireEvents(row, items) {
    var viewer = null;
    row.addEventListener('click', function (event) {
      var card = event.target.closest('.w99-042-rw__card');
      if (!card) return;
      // New-tab and modified clicks keep the card's own link to the product.
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var index = -1;
      items.forEach(function (item, i) { if (item.card === card) index = i; });
      if (index < 0) return;
      event.preventDefault();
      if (!viewer) viewer = new Viewer(row, items);
      viewer.open(index, 'card');
    });
    if (!('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      capture('real_walls_row_impression', { cards: items.length, layout: layout() });
    }, { threshold: 0.5 });
    observer.observe(row);
  }

  // The view: the photo beside its words and the piece in it. One dialog for the page, attached to <body> so no
  // sticky or transformed ancestor can trap it.
  function Viewer(row, items) {
    this.row = row;
    this.track = row.querySelector('.w99-042-rw__track');
    this.items = items;
    this.position = 0;
    this.restore = null;
    this.touch = null;

    var root = create('div', 'w99-042-view');
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-labelledby', 'w99-042-view-credit');
    root.innerHTML =
      '<div class="w99-042-view__backdrop" data-w99-042-view-close></div>' +
      '<div class="w99-042-view__dialog">' +
        '<div class="w99-042-view__media">' +
          '<img class="w99-042-view__image" alt="" width="900" height="1125">' +
          '<p class="w99-042-view__count" aria-hidden="true"></p>' +
          '<button type="button" class="w99-042-view__step w99-042-view__step--prev" aria-label="Previous photo" data-w99-042-view-step="-1">' + CHEVRON_PREV + '</button>' +
          '<button type="button" class="w99-042-view__step w99-042-view__step--next" aria-label="Next photo" data-w99-042-view-step="1">' + CHEVRON_NEXT + '</button>' +
        '</div>' +
        '<div class="w99-042-view__panel">' +
          '<div class="w99-042-view__words">' +
            '<p class="w99-042-view__quote"></p>' +
            '<a class="w99-042-view__source" target="_blank" rel="noopener" data-w99-042-view-source>' +
              '<span class="w99-042-view__credit" id="w99-042-view-credit"></span>' + EXTERNAL +
              '<span class="visually-hidden">(opens the original in a new tab)</span>' +
            '</a>' +
          '</div>' +
          '<div class="w99-042-view__shop">' +
            '<p class="w99-042-view__label">In this photo</p>' +
            '<div class="w99-042-view__pieces"></div>' +
          '</div>' +
        '</div>' +
      '</div>' +
      '<button type="button" class="w99-042-view__button w99-042-view__close" aria-label="Close" data-w99-042-view-close>' + CLOSE + '</button>' +
      '<button type="button" class="w99-042-view__button w99-042-view__nav w99-042-view__nav--prev" aria-label="Previous photo" data-w99-042-view-step="-1">' + CHEVRON_PREV + '</button>' +
      '<button type="button" class="w99-042-view__button w99-042-view__nav w99-042-view__nav--next" aria-label="Next photo" data-w99-042-view-step="1">' + CHEVRON_NEXT + '</button>';
    document.body.appendChild(root);

    this.root = root;
    this.dialog = root.querySelector('.w99-042-view__dialog');
    this.media = root.querySelector('.w99-042-view__media');
    this.image = root.querySelector('.w99-042-view__image');
    this.credit = root.querySelector('.w99-042-view__credit');
    this.source = root.querySelector('[data-w99-042-view-source]');
    this.count = root.querySelector('.w99-042-view__count');
    this.quote = root.querySelector('.w99-042-view__quote');
    this.shop = root.querySelector('.w99-042-view__shop');
    this.pieces = root.querySelector('.w99-042-view__pieces');
    this.panel = root.querySelector('.w99-042-view__panel');

    var self = this;
    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-w99-042-view-close]')) { self.close(); return; }
      var stepper = event.target.closest('[data-w99-042-view-step]');
      if (stepper) { self.step(Number(stepper.getAttribute('data-w99-042-view-step')), 'arrow'); return; }
      var source = event.target.closest('[data-w99-042-view-source]');
      if (source && source.hasAttribute('href')) {
        var shown = self.items[self.position];
        capture('real_walls_source_click', { product: shown.product, position: self.position + 1, credit: shown.credit, source: shown.source, layout: layout() });
        return;
      }
      var link = event.target.closest('[data-w99-042-piece-link]');
      if (link) {
        var item = self.items[self.position];
        capture('real_walls_product_click', { product: link.getAttribute('data-w99-042-piece-link'), position: self.position + 1, credit: item.credit, layout: layout() });
      }
    });
    root.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') { event.preventDefault(); self.close(); return; }
      if (event.key === 'ArrowLeft') { event.preventDefault(); self.step(-1, 'key'); return; }
      if (event.key === 'ArrowRight') { event.preventDefault(); self.step(1, 'key'); return; }
      if (event.key === 'Tab') self.trap(event);
    });
    // Swipe between photos on the photo; vertical movement is left to scrolling.
    this.media.addEventListener('touchstart', function (event) {
      var t = event.touches[0];
      self.touch = t ? { x: t.clientX, y: t.clientY } : null;
    }, { passive: true });
    this.media.addEventListener('touchend', function (event) {
      var t = event.changedTouches[0];
      if (!self.touch || !t) return;
      var dx = t.clientX - self.touch.x;
      var dy = t.clientY - self.touch.y;
      self.touch = null;
      if (Math.abs(dx) > SWIPE_MIN && Math.abs(dx) > Math.abs(dy) * 1.5) self.step(dx < 0 ? 1 : -1, 'swipe');
    }, { passive: true });
    var single = items.length < 2;
    Array.prototype.forEach.call(root.querySelectorAll('[data-w99-042-view-step]'), function (button) { button.hidden = single; });
  }

  Viewer.prototype.open = function (index, via) {
    if (index < 0 || index >= this.items.length) return;
    this.lockScroll();
    this.show(index, via);
    this.root.hidden = false;
    document.documentElement.classList.add(OPEN_CLASS);
    if (!REDUCED_MOTION && this.dialog.animate) {
      this.root.querySelector('.w99-042-view__backdrop').animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease-out' });
      this.dialog.animate([{ opacity: 0, transform: 'scale(1.015)' }, { opacity: 1, transform: 'none' }], { duration: 260, easing: 'cubic-bezier(.2,.7,.2,1)' });
    }
    this.root.querySelector('.w99-042-view__close').focus({ preventScroll: true });
  };

  // Closing returns focus to the card last shown, scrolled into the row, so stepping through the view leaves the row
  // where the visitor ended up.
  Viewer.prototype.close = function () {
    if (this.root.hidden) return;
    this.root.hidden = true;
    document.documentElement.classList.remove(OPEN_CLASS);
    this.unlockScroll();
    var card = this.items[this.position].card;
    var item = card.parentNode;
    if (this.track && item) {
      var trackBox = this.track.getBoundingClientRect();
      var itemBox = item.getBoundingClientRect();
      if (itemBox.left < trackBox.left - 1 || itemBox.right > trackBox.right + 1) {
        var pad = parseFloat(window.getComputedStyle(this.track).scrollPaddingLeft) || 0;
        this.track.scrollTo({ left: Math.max(0, this.track.scrollLeft + itemBox.left - trackBox.left - pad), behavior: 'auto' });
      }
    }
    card.focus({ preventScroll: true });
  };

  Viewer.prototype.step = function (direction, via) {
    if (this.items.length < 2) return;
    this.show((this.position + direction + this.items.length) % this.items.length, via);
    if (!REDUCED_MOTION && this.media.animate) this.media.animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });
  };

  Viewer.prototype.show = function (index, via) {
    this.position = index;
    var item = this.items[index];
    this.image.srcset = item.zoomSet;
    this.image.sizes = layout() === 'desktop' ? Math.round(Math.min(820, window.innerHeight - 160) * 0.8) + 'px' : '100vw';
    this.image.src = item.zoom;
    this.image.alt = item.alt;
    this.credit.innerHTML = item.creditHtml;
    if (item.sourceUrl) this.source.setAttribute('href', item.sourceUrl);
    else this.source.removeAttribute('href');
    this.count.textContent = this.items.length > 1 ? (index + 1) + ' / ' + this.items.length : '';
    this.quote.textContent = item.quote;
    this.pieces.textContent = '';
    this.shop.hidden = !item.piece;
    this.panel.classList.toggle('w99-042-view__panel--words-only', !item.piece);
    if (item.piece) this.pieces.appendChild(item.piece.content.cloneNode(true));
    this.dialog.scrollTop = 0;
    this.panel.scrollTop = 0;
    this.preload(index);
    capture('real_walls_view_opened', { product: item.product, position: index + 1, credit: item.credit, via: via, layout: layout() });
  };

  // The neighbouring photos load in the background, so stepping shows them at once.
  Viewer.prototype.preload = function (index) {
    var self = this;
    [index - 1, index + 1].forEach(function (i) {
      var item = self.items[(i + self.items.length) % self.items.length];
      if (!item || item.preloaded) return;
      var img = new Image();
      img.sizes = self.image.sizes;
      img.srcset = item.zoomSet;
      img.src = item.zoom;
      item.preloaded = true;
    });
  };

  Viewer.prototype.trap = function (event) {
    var focusable = Array.prototype.filter.call(this.root.querySelectorAll('button, a[href]'), function (el) {
      return !el.hidden && el.offsetParent !== null;
    });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  // Fixing <body> in place stops the page behind from scrolling on iOS Safari too; the position is restored on close.
  Viewer.prototype.lockScroll = function () {
    var body = document.body;
    var gap = window.innerWidth - document.documentElement.clientWidth;
    this.restore = {
      y: window.scrollY || window.pageYOffset || 0,
      style: { position: body.style.position, top: body.style.top, left: body.style.left, right: body.style.right, width: body.style.width, paddingRight: body.style.paddingRight }
    };
    body.style.position = 'fixed';
    body.style.top = -this.restore.y + 'px';
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    if (gap > 0) body.style.paddingRight = gap + 'px';
  };

  Viewer.prototype.unlockScroll = function () {
    if (!this.restore) return;
    var body = document.body;
    var style = this.restore.style;
    Object.keys(style).forEach(function (key) { body.style[key] = style[key]; });
    var html = document.documentElement;
    var behavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, this.restore.y);
    html.style.scrollBehavior = behavior;
    this.restore = null;
  };

  // The slideshow stays in the page, hidden, so its autoplay is paused.
  function pauseSlideshow(section) {
    try {
      var hero = section.querySelector('.flickity-enabled');
      var flickity = hero && window.Flickity && window.Flickity.data(hero);
      if (flickity && typeof flickity.pausePlayer === 'function') flickity.pausePlayer();
    } catch (error) {}
  }

  function applyTest() {
    var section = getSection();
    var template = getTemplate();
    if (!section || !template || section.querySelector('[data-w99-042-row]')) return;
    var host = section.querySelector('[data-section-type="slideshow-section"]') || section;
    var row = template.content.firstElementChild.cloneNode(true);
    host.appendChild(row);
    section.classList.add(TEST_CLASS);
    pauseSlideshow(section);
    wireArrows(row);
    wireEvents(row, readCards(row));
  }

  function logAssignment(variant) {
    if (!variant) return;
    try {
      var info = { variant: variant, source: IS_FORCED ? 'URL parameter (PostHog not called)' : 'PostHog' };
      var ph = window.posthog;
      if (!IS_FORCED && ph && ph.__loaded) {
        info.flagValue = ph.getFeatureFlag(FLAG, { send_event: false });
        var details = ph.featureFlags && typeof ph.featureFlags.getFeatureFlagDetails === 'function'
          ? ph.featureFlags.getFeatureFlagDetails(FLAG) : null;
        if (details && details.reason) info.reason = details.reason.description || details.reason.code;
        info.deviceId = (ph.get_property && ph.get_property('$device_id')) || null;
        if (!info.deviceId) {
          info.note = 'This browser has no PostHog device ID, so this device-bucketed flag returns false and control is shown.';
        } else if (info.flagValue === undefined || info.flagValue === null) {
          info.note = 'PostHog returned no value for this flag before the timeout, so control is shown.';
        }
      }
      console.info('[' + FLAG + ']', info);
    } catch (error) {}
  }

  // Sent only when PostHog itself assigned the variant: a forced preview, a fallback to control or a device PostHog
  // could not bucket is not in the experiment, so it must not activate anyone.
  function trackSlotReached(variant) {
    if (!variant || IS_FORCED) return;
    var section = getSection();
    var ph = window.posthog;
    if (!section || !ph || !ph.__loaded) return;
    var flagValue;
    try { flagValue = ph.getFeatureFlag(FLAG, { send_event: false }); } catch (error) { return; }
    if (flagValue !== variant) return;
    whenNear(section).then(function () {
      var properties = { layout: layout() };
      properties['$feature/' + FLAG] = variant;
      capture('real_walls_slot_reached', properties);
    });
  }

  function start() {
    var root = document.documentElement;
    if (root.hasAttribute('data-w99-042-started')) return;
    root.setAttribute('data-w99-042-started', '');
    if (typeof window.runExperiment !== 'function') return;

    window.runExperiment(FLAG, {
      debug: DEBUG,
      isEligible: isEligible,
      variants: {
        control: [],
        test: function () { applyTest(); }
      }
    }).then(function (variant) {
      logAssignment(variant);
      trackSlotReached(variant);
    }).catch(function (error) {
      console.warn(FLAG + ': defaulting to control', error);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
